import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AvatarUpload from "./AvatarUpload";
import BrandLogo from "./BrandLogo";
import { Search, Settings, User, ChevronDown, Moon, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { NotificationDropdown } from "./NotificationDropdown";

interface TopHeaderProps {
  userName: string;
  userRole: string;
  userEmail: string;
}

const TopHeader = ({ userName, userRole, userEmail }: TopHeaderProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [caseSuggestions, setCaseSuggestions] = useState<Array<{ id: string; title: string; case_number?: string; case_number_cnj?: string }>>([]);
  const [clientSuggestions, setClientSuggestions] = useState<Array<{ id: string; name: string; email?: string }>>([]);
  const blurTimeoutRef = useRef<number | null>(null);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    document.documentElement.classList.toggle('dark');
  };

  // Autocomplete: buscar sugestões conforme a página atual
  useEffect(() => {
    const q = searchQuery.trim();
    if (blurTimeoutRef.current) {
      window.clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    if (q.length < 2) {
      setCaseSuggestions([]);
      setClientSuggestions([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const path = location.pathname;
        const wantCases = path.startsWith('/cases') || path.startsWith('/processo') || (!path.startsWith('/clients') && !path.startsWith('/documents'));
        const wantClients = path.startsWith('/clients') || (!path.startsWith('/cases') && !path.startsWith('/documents'));

        const requests: Promise<void>[] = [];

        if (wantCases) {
          requests.push((async () => {
            // Buscar processos por título, número (CNJ e interno) e também por nome de cliente
            const aggregated: any[] = [];

            // 1) Busca direta por campos do processo
            const { data: byFields, error: byFieldsErr } = await supabase
              .from('cases')
              .select('id, title, case_number, case_number_cnj, client_id')
              .or(`title.ilike.%${q}%,case_number.ilike.%${q}%,case_number_cnj.ilike.%${q}%`)
              .limit(50);
            if (!byFieldsErr && byFields) aggregated.push(...byFields);

            // 2) Busca clientes por nome e adiciona processos desses clientes
            const { data: clientsMatch, error: clientsErr } = await supabase
              .from('clients')
              .select('id')
              .ilike('name', `%${q}%`)
              .limit(50);

            if (!clientsErr && clientsMatch && clientsMatch.length) {
              const clientIds = clientsMatch.map((c: any) => c.id);
              const { data: byClient, error: byClientErr } = await supabase
                .from('cases')
                .select('id, title, case_number, case_number_cnj, client_id')
                .in('client_id', clientIds)
                .limit(50);
              if (!byClientErr && byClient) aggregated.push(...byClient);
            }

            // Remover duplicados por id e limitar
            const uniqueMap = new Map<string, any>();
            for (const c of aggregated) {
              if (!uniqueMap.has(c.id)) uniqueMap.set(c.id, c);
            }
            const final = Array.from(uniqueMap.values()).slice(0, 50);
            setCaseSuggestions(final as any);
          })());
        } else {
          setCaseSuggestions([]);
        }

        if (wantClients) {
          requests.push((async () => {
            const { data, error } = await supabase
              .from('clients')
              .select('id, name, email')
              .ilike('name', `%${q}%`)
              .limit(5);
            if (!error) setClientSuggestions((data || []) as any);
          })());
        } else {
          setClientSuggestions([]);
        }

        await Promise.all(requests);
      } catch (_) {
        // Silenciar erros de autocomplete em dev
      } finally {
        setIsLoading(false);
      }
    }, 250);
    return () => window.clearTimeout(timer);
  }, [searchQuery, location.pathname]);

  return (
    <div className="flex-1 flex items-center justify-between bg-background px-2 xs:px-3 sm:px-4 md:px-6">
      {/* Logo e Brand */}
      <div className="flex items-center flex-shrink-0">
        <BrandLogo size="sm" showText={false} className="xs:block sm:hidden" />
        <div className="hidden sm:block">
          <h1 className="text-xs xs:text-sm sm:text-lg font-bold text-foreground ios-text-responsive">ALLANA STHÉFANY</h1>
          <p className="text-xs text-muted-foreground hidden md:block">Advocacia & Consultoria Jurídica</p>
        </div>
      </div>

      {/* Barra de Busca Central */}
      <div className="flex-1 max-w-md mx-2 xs:mx-3 sm:mx-4 hidden sm:block">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar clientes, casos, documentos..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const q = searchQuery.trim();
                if (q) navigate(`/cases?q=${encodeURIComponent(q)}`);
              }
            }}
            className="pl-9 pr-10 bg-muted/50 border-border focus:bg-background transition-colors ios-no-zoom"
          />
          {isOpen && (caseSuggestions.length > 0 || clientSuggestions.length > 0 || isLoading) && (
            <div
              className="absolute left-0 right-0 mt-2 z-50 rounded-md border bg-background shadow max-h-72 overflow-y-auto"
              onMouseDown={(e) => e.preventDefault()}
            >
              {isLoading && (
                <div className="px-3 py-2 text-xs text-muted-foreground">Carregando…</div>
              )}
              {caseSuggestions.length > 0 && (
                <div>
                  <div className="px-3 py-1 text-[11px] text-muted-foreground">Processos</div>
                  {caseSuggestions.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-muted/50 text-sm"
                      onClick={() => {
                        // Navega para a página de detalhes do processo
                        navigate(`/processo/${c.id}`);
                        setIsOpen(false);
                      }}
                    >
                      {(c.title || 'Sem título')}
                      {c.case_number_cnj ? ` • ${c.case_number_cnj}` : c.case_number ? ` • ${c.case_number}` : ''}
                    </button>
                  ))}
                </div>
              )}
              {clientSuggestions.length > 0 && (
                <div>
                  <div className="px-3 py-1 text-[11px] text-muted-foreground">Clientes</div>
                  {clientSuggestions.map((cl) => (
                    <button
                      key={cl.id}
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-muted/50 text-sm"
                      onClick={() => {
                        // Sem página de detalhes de cliente no desktop: usa filtro na lista
                        navigate(`/clients?q=${encodeURIComponent(cl.name)}`);
                        setIsOpen(false);
                      }}
                    >
                      {cl.name}
                      {cl.email ? ` • ${cl.email}` : ''}
                    </button>
                  ))}
                </div>
              )}
              {!isLoading && caseSuggestions.length === 0 && clientSuggestions.length === 0 && (
                <div className="px-3 py-2 text-xs text-muted-foreground">Nenhum resultado</div>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={() => {
              const q = searchQuery.trim();
              if (q) navigate(`/cases?q=${encodeURIComponent(q)}`);
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 text-xs rounded hover:bg-muted text-muted-foreground"
            aria-label="Buscar"
          >
            Buscar
          </button>
        </div>
      </div>

      {/* Ações do Header */}
      <div className="flex items-center gap-1 xs:gap-1 sm:gap-2">
        {/* Busca Mobile */}
        <Button 
          variant="ghost" 
          size="icon" 
          className="sm:hidden ios-touch-target ios-no-highlight"
          onBlur={() => {
            // pequeno atraso para permitir clique nas sugestões
            blurTimeoutRef.current = window.setTimeout(() => setIsOpen(false), 120);
          }}
          onClick={() => {
            const q = searchQuery.trim();
            if (q) navigate(`/cases?q=${encodeURIComponent(q)}`);
          }}
        >
          <Search className="h-4 w-4 xs:h-5 xs:w-5" />
        </Button>

        {/* Notificações */}
        <div className="ios-touch-target">
          <NotificationDropdown />
        </div>

        {/* Toggle Dark Mode - Oculto em mobile para economizar espaço */}
        <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="hidden sm:flex ios-touch-target ios-no-highlight">
          {isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>

        {/* Configurações - Oculto em mobile */}
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate('/settings')}
          className="hidden sm:flex ios-touch-target ios-no-highlight"
        >
          <Settings className="h-5 w-5" />
        </Button>

        {/* Perfil do Usuário */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-1 xs:gap-2 px-1 xs:px-2 sm:px-3 ios-touch-target ios-no-highlight">
              <AvatarUpload 
                userName={userName} 
                size="sm" 
                showUploadButton={false}
              />
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-foreground">{userName}</p>
                <p className="text-xs text-muted-foreground">{userRole}</p>
              </div>
              <ChevronDown className="h-3 w-3 xs:h-4 xs:w-4 text-muted-foreground hidden md:block" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="p-3 border-b">
              <p className="font-medium text-foreground">{userName}</p>
              <p className="text-sm text-muted-foreground">{userEmail}</p>
              <Badge variant="secondary" className="mt-1 text-xs">
                {userRole}
              </Badge>
            </div>
            <DropdownMenuItem 
              className="cursor-pointer"
              onClick={() => navigate('/profile')}
            >
              <User className="mr-2 h-4 w-4" />
              Meu Perfil
            </DropdownMenuItem>
            <DropdownMenuItem 
              className="cursor-pointer"
              onClick={() => navigate('/settings')}
            >
              <Settings className="mr-2 h-4 w-4" />
              Configurações
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              className="cursor-pointer text-destructive"
              onClick={async () => {
                await supabase.auth.signOut();
                navigate('/auth');
              }}
            >
              Sair da Conta
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default TopHeader;